import { pluralize } from '@frontend/duck-tape/pluralize';
import dayjs, { type Dayjs as DayjsType } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isToday from 'dayjs/plugin/isToday';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { makeDateISOString } from '../brandedTypes/dateIsoString';
import { entries, groupBy, sortBy } from '../hidash/hidash';

dayjs.extend(duration);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isToday);
dayjs.extend(quarterOfYear);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

export type FormattableDate = Date | DateISOString | DayjsType | string;

type FormatDateFunction = (date: FormattableDate, options?: DateOptions) => string;

type DateOptions = {
  strict?: boolean;
  utc?: boolean;
};

export type SupportedDateFormat = keyof typeof dateFormatters;

dayjs.tz.setDefault('America/New_York');

// Intentionally not exported
const formatDate = (date: FormattableDate, format: string): string => dayjs(date).format(format);

export const ISO_8601_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
export const ISO_8601_FORMAT_WITH_TIMEZONE = 'YYYY-MM-DDTHH:mm:ssZ';
export const DATE_ISO_STRING_FORMAT = 'YYYY-MM-DD';

export const validateDate = (date: string, format: string, strict = true): boolean =>
  dayjs(date, format, strict).isValid();

export const dateFormatters = {
  /** @example 2018/08/16 */
  DATE_ISO_STRING_FORMAT: ((date) => formatDate(date, DATE_ISO_STRING_FORMAT)) as FormatDateFunction,
  /** @example August 16, 2018 */
  LL: ((date) => formatDate(date, 'LL')) as FormatDateFunction,
  /** @example August 16, 2018 12:00am */
  LLLL: ((date) => formatDate(date, 'LLLL')) as FormatDateFunction,
  'M/D': ((date) => formatDate(date, 'M/D')) as FormatDateFunction,
  'MM/DD/YY': ((date) => formatDate(date, 'MM/DD/YY')) as FormatDateFunction,
  /** @example 08/16/2018 */
  'MM/DD/YYYY': ((date) => formatDate(date, 'L')) as FormatDateFunction,
  /** @example Aug 16 */
  'MMM D': ((date) => formatDate(date, 'MMM D')) as FormatDateFunction,
  'MMM D, YYYY': ((date) => formatDate(date, 'MMM D, YYYY')) as FormatDateFunction,
  /** @example Aug 16, 12:00am */
  'MMM D, h:mma': ((date) => formatDate(date, 'MMM D, h:mma')) as FormatDateFunction,
  /** @example Mon, May 2 */
  'ddd, MMM D': ((date) => formatDate(date, 'ddd, MMM D')) as FormatDateFunction,
  'ddd, MMM D, YYYY': ((date) => formatDate(date, 'ddd, MMM D, YYYY')) as FormatDateFunction,
  /** @example Tuesday */
  dddd: ((date) => formatDate(date, 'dddd')) as FormatDateFunction,
  friendlyDate: ((date) => {
    const then = dayjs(date);
    const now = dayjs();
    if (then.isSame(now, 'day')) {
      return 'Today';
    } else if (then.isSame(now.subtract(1, 'day'), 'day')) {
      return 'Yesterday';
    } else {
      return formatDate(date, 'ddd, MMM D');
    }
  }) as FormatDateFunction,
  /** @example 12:00 AM */
  'h:mm A': ((date) => formatDate(date, 'h:mm A')) as FormatDateFunction,
  /** @example 12am */
  ha: ((date) => formatDate(date, 'ha')) as FormatDateFunction,
  'hh:mm a': ((date) => formatDate(date, 'hh:mm a')) as FormatDateFunction,
  /** @example 2018-08-16T12:00:00.000Z */
  iso8601: ((date) => dayjs(date).toISOString()) as FormatDateFunction,
  message: ((date) => {
    const then = dayjs(date);
    const now = dayjs();
    if (then.isSame(now, 'day')) {
      return formatDate(date, '[Today] [at] h:mma');
    } else if (then.isSame(now.subtract(1, 'day'), 'day')) {
      return formatDate(date, '[Yesterday] [at] h:mma');
    } else if (then.isAfter(now.subtract(7, 'day'), 'day')) {
      return formatDate(date, 'dddd [at] h:mma');
    } else {
      return formatDate(date, 'ddd, MMM D [at] h:mma');
    }
  }) as FormatDateFunction,
  /** @example 8 weeks ago */
  relativeWeeksAgo: ((date: FormattableDate): string => {
    const then = dayjs(date);
    const now = dayjs();
    const diffDays = now.diff(then, 'day');
    const diffWeeks = now.diff(then, 'week');

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays <= 7) {
      return 'Last week';
    } else {
      return `${diffWeeks} ${pluralize('week', diffWeeks)} ago`;
    }
  }) as FormatDateFunction,
  roundedTimeSince: ((date: FormattableDate): string => {
    const then = dayjs(date);
    const now = dayjs();
    const timeDifference = now.diff(then);
    const durationObject = dayjs.duration(timeDifference);

    // Get the largest unit of time present in the duration
    if (durationObject.years() > 0) {
      return durationObject.years() + 'y';
    } else if (durationObject.months() > 0) {
      return durationObject.months() + 'mo';
    } else if (durationObject.days() > 0) {
      return durationObject.days() + 'd';
    } else if (durationObject.hours() > 0) {
      return durationObject.hours() + 'h';
    } else if (durationObject.minutes() > 0) {
      return durationObject.minutes() + 'm';
    } else if (durationObject.seconds() > 0) {
      return durationObject.seconds() + 's';
    } else {
      return '0s';
    }
  }) as FormatDateFunction,
} as const;

const isAllDayDateString = (value: string): boolean => !value.includes('T');

/**
 * Formats a date-time range as `ddd, MMM D · h:mm A` (no end) or
 * `ddd, MMM D · h:mm A — h:mm A` (with end).
 *
 * Also handles all-day events: when `startsAt` is a `YYYY-MM-DD` date string,
 * the range is formatted as `ddd, MMM D · All day` (single day) or
 * `ddd, MMM D — ddd, MMM D · All day` (multi-day). gcal's end date for
 * all-day events is exclusive, so it's shifted back by one day before display.
 * @example "Mon, May 2 · 1:30 PM — 2:30 PM"
 * @example "Tue, May 12 · All day"
 * @example "Tue, May 12 — Thu, May 14 · All day"
 */
export const formatDateTimeRange = (
  startsAt: DateISOString | DateTimeISOString,
  endsAt: MaybeUndefined<DateISOString | DateTimeISOString>,
): string => {
  if (isAllDayDateString(startsAt)) {
    const startDate = dayjs.utc(startsAt);
    const startLabel = startDate.format('ddd, MMM D');
    if (!endsAt) return `${startLabel} · All day`;
    const inclusiveEnd = dayjs.utc(endsAt).subtract(1, 'day');
    if (inclusiveEnd.isSame(startDate, 'day')) return `${startLabel} · All day`;
    return `${startLabel} — ${inclusiveEnd.format('ddd, MMM D')} · All day`;
  }
  const datePart = dateFormatters['ddd, MMM D'](startsAt);
  const startTime = dateFormatters['h:mm A'](startsAt);
  if (!endsAt) return `${datePart} · ${startTime}`;
  return `${datePart} · ${startTime} — ${dateFormatters['h:mm A'](endsAt)}`;
};

const groupByDateBuckets = {
  '10 months ago': [],
  '11 months ago': [],
  '2 days ago': [],
  '2 months ago': [],
  '2 weeks ago': [],
  '3 days ago': [],
  '3 months ago': [],
  '3 weeks ago': [],
  '4 days ago': [],
  '4 months ago': [],
  '5 days ago': [],
  '5 months ago': [],
  '6 days ago': [],
  '6 months ago': [],
  '7 months ago': [],
  '8 months ago': [],
  '9 months ago': [],
  'A month ago': [],
  'Last week': [],
  'Last year': [],
  Today: [],
  Yesterday: [],
};

type GroupedByDate<T> = Record<keyof typeof groupByDateBuckets, T[]>;

const parseBucketKey = (key: string): number => {
  if (key === 'Today') return 0;
  if (key === 'Yesterday') return 1;
  if (key === 'Last week') return 7;
  if (key === 'Last year') return 365;
  if (key === 'A month ago') return 30;

  const [value, unit] = key.split(' ');
  const numberValue = parseInt(value ?? '', 10);

  switch (unit) {
    case 'days':
      return numberValue;
    case 'weeks':
      return numberValue * 7;
    case 'months':
      return numberValue * 30;
    case 'years':
      return numberValue * 365;
    default:
      return Infinity;
  }
};

export const groupByDate = <T>(data: T[], getDate: (t: T) => FormattableDate): GroupedByDate<T> => {
  const getBucket = (item: T) => {
    const today = dayjs();
    const createdAt = dayjs(getDate(item));
    const diffDays = today.diff(createdAt, 'day');
    const diffWeeks = today.diff(createdAt, 'week');
    const diffMonths = today.diff(createdAt, 'month');
    const diffYears = today.diff(createdAt, 'year');

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays >= 2 && diffDays <= 6) {
      return `${diffDays} days ago`;
    } else if (diffWeeks === 1) {
      return 'Last week';
    } else if (diffWeeks === 2 || diffWeeks === 3) {
      return `${diffWeeks} weeks ago`;
    } else if (diffMonths === 1) {
      return 'A month ago';
    } else if (diffMonths >= 2 && diffMonths <= 11) {
      return `${diffMonths} months ago`;
    } else if (diffYears >= 1) {
      return 'Last year';
    }

    return 'Other';
  };

  return { ...groupByDateBuckets, ...groupBy(data, getBucket) };
};

export const sortedGroupByDate = <T>(groupedData: GroupedByDate<T>) => {
  // Convert groupedData to an array of [key, value] pairs
  const dataEntries = entries(groupedData) as [keyof typeof groupByDateBuckets, T[]][];

  // Sort the entries based on the parsed bucket key in descending order
  const sortedEntries = sortBy(dataEntries, ([key]) => parseBucketKey(key));

  return sortedEntries;
};

export const getIsLessThanTwoWeeksOld = (datetime: string) => dayjs().diff(dayjs(datetime), 'd', true) < 14;

export const isExpiringWithinDays = (expirationDate: string, days: number): boolean => {
  const diffDays = dayjs(expirationDate).diff(dayjs(), 'day');
  return diffDays <= days && diffDays >= 0;
};

export const isDateWithinDays = (date: DateTimeISOString, days: number): boolean => {
  const lastCommentDate = dayjs(date);
  const now = dayjs();
  return now.diff(lastCommentDate, 'day') <= days;
};

/**
 * Birthday input mask utilities for converting between display (MM/DD/YYYY) and ISO (YYYY-MM-DD) formats.
 * Used by both web and mobile BirthdayInputMask components.
 */

/**
 * Convert ISO date (YYYY-MM-DD) to display format (MM/DD/YYYY)
 */
export const birthdayIsoToDisplay = (isoDate: string | undefined): string => {
  if (!isoDate) return '';
  return dateFormatters['MM/DD/YYYY'](isoDate);
};

/**
 * Convert display format (MM/DD/YYYY) to ISO date (YYYY-MM-DD)
 * Returns null if the date is incomplete or invalid
 */
export const isValidBirthday = (cleaned: string): cleaned is `${string}/${string}/${string}` => {
  const parsed = dayjs(cleaned, 'MM/DD/YYYY', true);
  if (!parsed.isValid()) {
    return false;
  }

  return parsed.year() <= dayjs().year();
};

export const birthdayDisplayToIso = (displayDate: string): DateISOString | null => {
  const cleaned = displayDate.replace(/[^\d/]/g, '');
  if (cleaned.length !== 10) return null;
  if (!isValidBirthday(cleaned)) return null;

  const [month, day, year] = cleaned.split('/');
  return makeDateISOString(`${year}-${month}-${day}`);
};

/**
 * Format input as user types to MM/DD/YYYY pattern
 * Automatically adds slashes as the user types digits
 */
export const formatBirthdayInput = (input: string): string => {
  // Remove all non-digit characters
  const digits = input.replace(/\D/g, '');

  // Limit to 8 digits (MMDDYYYY)
  const limited = digits.slice(0, 8);

  // Format with slashes
  if (limited.length <= 2) {
    return limited;
  } else if (limited.length <= 4) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
  }
};

export const meetsBirthdayRequirements = (isoDate: string, maxAge: number, minAge: number): boolean => {
  const birthday = dayjs(isoDate);
  if (!birthday.isValid()) return false;

  const today = dayjs();
  if (birthday.isAfter(today, 'day')) return false;

  const age = today.diff(birthday, 'year');
  return age >= minAge && age <= maxAge;
};

export { dayjs, DayjsType };
